/**
 * Every flag the deployed engine supports, rendered from the engine's own
 * catalogue.
 *
 * NOTHING HERE IS A HARDCODED LIST OF OPTIONS. The controls, their permitted
 * values and their defaults all come from `/api/pdflab/options`, which the
 * parser pod serves out of the installed package's generated option list. The
 * only local knowledge is which GROUP a flag belongs in, and an unrecognised
 * flag falls into "Other" rather than disappearing — so pinning a newer engine
 * surfaces its new options instead of hiding them.
 */

import { useMemo, useState } from "react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { Card, Checkbox, Input, Select } from "@/components/ui";
import { enumValues, isMultiOption, optionLabel, type OptionDef } from "@/lib/pdflab";

/**
 * Grouping, by flag name. Order within a group is the order the engine
 * declares them, so the panel reads like the CLI reference.
 */
const GROUPS: { id: string; label: string; blurb: string; names: string[] }[] = [
  {
    id: "output",
    label: "Output",
    blurb: "Which artefacts to produce, and how images are carried.",
    names: [
      "format", "markdown-with-html", "image-output", "image-format",
      "markdown-page-separator", "text-page-separator", "html-page-separator",
      "image-resolution",
    ],
  },
  {
    id: "parsing",
    label: "Parsing",
    blurb: "How the page is read: reading order, tables, what counts as content.",
    names: [
      "reading-order", "table-method", "use-struct-tree", "pages",
      "include-header-footer", "detect-strikethrough", "keep-line-breaks",
      "space-ratio", "replace-invalid-chars", "threads", "password",
    ],
  },
  {
    id: "safety",
    label: "Content safety",
    blurb:
      "On by default, and worth understanding before switching off: the parser drops text a human cannot see — hidden layers, off-page content, near-zero-size glyphs, text painted the same colour as its background. That is the documented defence against a PDF that carries instructions meant for whatever reads it next.",
    names: ["content-safety-off", "sanitize"],
  },
  {
    id: "hybrid",
    label: "Hybrid",
    blurb:
      "Sends pages to the docling backend. This is the only way a SCANNED page produces anything — the native parser reads the content stream, which an image-only page does not have.",
    names: ["hybrid", "hybrid-mode", "hybrid-timeout", "hybrid-fallback"],
  },
];

export function OptionsPanel({
  options,
  values,
  reserved,
  onChange,
}: {
  options: OptionDef[];
  values: Record<string, any>;
  reserved: string[];
  onChange: (next: Record<string, any>) => void;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({ output: true, hybrid: true });

  const grouped = useMemo(() => {
    const byName = new Map(options.map((o) => [o.name, o]));
    const claimed = new Set<string>();
    const groups = GROUPS.map((g) => {
      const defs = g.names.map((n) => byName.get(n)).filter((d): d is OptionDef => Boolean(d));
      defs.forEach((d) => claimed.add(d.name));
      return { ...g, defs };
    });
    // Anything the engine gained that this file has not been told about. Shown,
    // not dropped: a new flag nobody can reach is worse than an ugly group.
    const rest = options.filter(
      (o) => !claimed.has(o.name) && !reserved.includes(o.python_name),
    );
    return rest.length
      ? [...groups, { id: "other", label: "Other", blurb: "Flags this version added.", defs: rest }]
      : groups;
  }, [options, reserved]);

  const set = (name: string, value: any) => onChange({ ...values, [name]: value });

  return (
    <div className="space-y-3">
      {grouped.map((group) => {
        const isOpen = open[group.id] ?? false;
        return (
          <Card key={group.id}>
            <button
              type="button"
              onClick={() => setOpen((o) => ({ ...o, [group.id]: !isOpen }))}
              className="fh-focus flex w-full cursor-pointer items-start gap-2 px-4 py-3 text-left"
            >
              <span className="mt-0.5 text-fh-text-muted">
                {isOpen ? <FiChevronDown /> : <FiChevronRight />}
              </span>
              <span className="min-w-0">
                <span className="block text-fh-body font-medium text-fh-text-strong">
                  {group.label}
                </span>
                <span className="mt-0.5 block text-fh-meta leading-relaxed text-fh-text-muted">
                  {group.blurb}
                </span>
              </span>
            </button>

            {isOpen && (
              <div className="space-y-4 border-t border-fh-border-soft px-4 py-4">
                {group.defs.map((def) => (
                  <OptionControl
                    key={def.name}
                    def={def}
                    value={values[def.python_name]}
                    onChange={(v) => set(def.python_name, v)}
                  />
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function OptionControl({
  def,
  value,
  onChange,
}: {
  def: OptionDef;
  value: any;
  onChange: (v: any) => void;
}) {
  const values = enumValues(def);
  const multi = isMultiOption(def);

  if (def.type === "boolean") {
    return (
      <div>
        <Checkbox
          label={optionLabel(def)}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
        <p className="ml-6 mt-1 text-fh-meta leading-relaxed text-fh-text-muted">{def.description}</p>
      </div>
    );
  }

  // A list-valued flag: checkboxes, because the alternative is a text input
  // where a typo becomes a silently ignored format.
  if (multi && values) {
    const selected: string[] = Array.isArray(value) ? value : [];
    return (
      <div>
        <p className="text-fh-row font-medium text-fh-text-mid">{optionLabel(def)}</p>
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1.5">
          {values.map((v) => (
            <Checkbox
              key={v}
              label={v}
              checked={selected.includes(v)}
              onChange={(e) =>
                onChange(
                  e.target.checked ? [...selected, v] : selected.filter((s) => s !== v),
                )
              }
            />
          ))}
        </div>
        <p className="mt-1 text-fh-meta leading-relaxed text-fh-text-muted">{def.description}</p>
      </div>
    );
  }

  if (values) {
    return (
      <div>
        <Select
          label={optionLabel(def)}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value || undefined)}
          placeholder={`default${def.default ? ` (${def.default})` : ""}`}
          options={values.map((v) => ({ value: v, label: v }))}
        />
        <p className="mt-1 text-fh-meta leading-relaxed text-fh-text-muted">{def.description}</p>
      </div>
    );
  }

  return (
    <div>
      <Input
        label={optionLabel(def)}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || undefined)}
        placeholder={def.default != null ? String(def.default) : ""}
        // Password is the one flag whose value is a secret rather than a
        // setting: it is the PDF's own open password.
        type={def.name === "password" ? "password" : "text"}
      />
      <p className="mt-1 text-fh-meta leading-relaxed text-fh-text-muted">{def.description}</p>
    </div>
  );
}

/** Compact one-line summary of what is set, for a run's header and saved runs. */
export function summariseOptions(values: Record<string, any>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(values)) {
    if (v === undefined || v === "" || v === false) continue;
    if (Array.isArray(v)) {
      if (v.length) parts.push(`${k}=${v.join("+")}`);
    } else if (v === true) {
      parts.push(k);
    } else {
      parts.push(`${k}=${v}`);
    }
  }
  return parts.join(" · ") || "defaults";
}
